import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input, InputLabel, InputGroup } from '../../../components/ui';

export default function UsersEdit({ user, roles, organizations = [] }) {
  const [formData, setFormData] = useState({
    email: user.email || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    role: user.role || 'student',
    password: '',
    grade_level: user.grade_level || '',
    organization_id: user.organization_id || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const payload = { ...formData };
    if (payload.role !== 'student') payload.grade_level = '';
    router.patch(`/admin/users/${user.id}`, { user: payload }, {
      onFinish: () => setIsSubmitting(false),
    });
  };

  return (
    <TeacherShell scrollTitle={<><Link href={`/admin/users/${user.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{user.full_name}</Link>{'  ›  Edit'}</>}>
      <TeacherPageHeader
        title="Edit User"
        subtitle={user.full_name}
        backLink={`/admin/users/${user.id}`}
        backLabel="Back to user"
      />

      <div className="max-w-2xl">
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InputGroup>
                <InputLabel htmlFor="first_name">First name</InputLabel>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={handleChange('first_name')}
                  placeholder="First name"
                />
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor="last_name">Last name</InputLabel>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={handleChange('last_name')}
                  placeholder="Last name"
                />
              </InputGroup>
            </div>

            <InputGroup>
              <InputLabel htmlFor="email">Email</InputLabel>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                placeholder="user@example.com"
              />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="role" required>Role</InputLabel>
              <select
                id="role"
                value={formData.role}
                onChange={handleChange('role')}
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-600 shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] text-gray-900 dark:text-white transition-all duration-200 focus:outline-none focus:border-gray-400 focus:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06),0_0_0_3px_rgba(156,163,175,0.2)] dark:focus:border-gray-500 dark:focus:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06),0_0_0_3px_rgba(107,114,128,0.3)]"
              >
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
            </InputGroup>

            {organizations.length > 0 && (
              <InputGroup>
                <InputLabel htmlFor="organization_id">Organization</InputLabel>
                <select
                  id="organization_id"
                  value={formData.organization_id}
                  onChange={handleChange('organization_id')}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-600 shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] text-gray-900 dark:text-white transition-all duration-200 focus:outline-none focus:border-gray-400 focus:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06),0_0_0_3px_rgba(156,163,175,0.2)] dark:focus:border-gray-500 dark:focus:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06),0_0_0_3px_rgba(107,114,128,0.3)]"
                >
                  <option value="">No organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </InputGroup>
            )}

            <InputGroup>
              <InputLabel htmlFor="password">Password</InputLabel>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                placeholder="Leave blank to keep current"
              />
            </InputGroup>

            {formData.role === 'student' && (
              <InputGroup>
                <InputLabel htmlFor="grade_level">Grade level</InputLabel>
                <Input
                  id="grade_level"
                  type="number"
                  min={1}
                  max={12}
                  value={formData.grade_level}
                  onChange={handleChange('grade_level')}
                  placeholder="1-12"
                />
              </InputGroup>
            )}

            <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
              <Link href={`/admin/users/${user.id}`}>
                <Button type="button" variant="ghost">Cancel</Button>
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </TeacherShell>
  );
}
